import { Check, Xmark } from 'iconoir-react';
import { useId } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import { PrimitiveButton } from '@savgroup-front-common/primitive/ui/button';
import { Progress } from '@savgroup-front-common/primitive/ui/progress';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';

import { PublicBar } from '../PublicBar/PublicBar';

import messages from './messages';
import {
  DONE_STEP_BADGE_CLASS_NAME,
  FAILED_STEP_BADGE_CLASS_NAME,
  PENDING_STEP_BADGE_CLASS_NAME,
  STEP_BADGE_CLASS_NAME,
  STEP_STATE,
} from './OnboardingSetUp.constants';
import {
  getProgressPercent,
  getStepMessage,
  getStepState,
  getStepStateMessage,
  PROVISIONING_STEPS,
} from './OnboardingSetUp.helpers';
import { useOnboardingSetUp } from './OnboardingSetUp.hooks';

const OnboardingSetUp = () => {
  const { currentStepIndex, isFailed, isRetrying, handleRetry } =
    useOnboardingSetUp();
  const progressLabelId = useId();

  const stepCount = PROVISIONING_STEPS.length;
  const isReady = currentStepIndex >= stepCount;
  const progressPercent = getProgressPercent(currentStepIndex);

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted-default stext-base-default">
      <PublicBar />
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="flex w-full max-w-120 flex-col gap-7">
          {isFailed && (
            <div
              role="alert"
              className="flex flex-col items-center gap-4 text-center"
            >
              <div className="flex flex-col gap-1.5">
                <h1 className="text-xl font-semibold text-pretty">
                  <SafeFormattedMessageWithoutSpread
                    message={messages.failedTitle}
                  />
                </h1>
                <p className="text-sm stext-muted-default text-pretty">
                  <SafeFormattedMessageWithoutSpread
                    message={messages.failedDescription}
                  />
                </p>
              </div>
              <PrimitiveButton
                type={BUTTON_TYPES.BUTTON}
                onClick={handleRetry}
                disabled={isRetrying}
                data-testid="onboardingRetryButton"
              >
                {isRetrying && <Spinner aria-hidden />}
                <SafeFormattedMessageWithoutSpread message={messages.retry} />
              </PrimitiveButton>
            </div>
          )}
          {!isFailed && (
            <div className="flex flex-col gap-1.5 text-center">
              <h1 className="text-xl font-semibold text-pretty">
                {isReady && (
                  <SafeFormattedMessageWithoutSpread
                    message={messages.readyTitle}
                  />
                )}
                {!isReady && (
                  <SafeFormattedMessageWithoutSpread message={messages.title} />
                )}
              </h1>
              <p className="text-sm stext-muted-default text-pretty">
                {isReady && (
                  <SafeFormattedMessageWithoutSpread
                    message={messages.readySubtitle}
                  />
                )}
                {!isReady && (
                  <SafeFormattedMessageWithoutSpread
                    message={messages.subtitle}
                  />
                )}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex justify-between text-xs">
              <span id={progressLabelId} className="stext-muted-default">
                {isReady && (
                  <SafeFormattedMessageWithoutSpread
                    message={messages.progressCompleted}
                  />
                )}
                {!isReady && (
                  <SafeFormattedMessageWithoutSpread
                    message={messages.stepCounter}
                    values={{ current: currentStepIndex + 1, total: stepCount }}
                  />
                )}
              </span>
              <span className="font-semibold stext-primary-default tabular-nums">
                <SafeFormattedMessageWithoutSpread
                  message={messages.progressPercent}
                  values={{ percent: progressPercent / 100 }}
                />
              </span>
            </div>
            <Progress
              value={progressPercent}
              track="subtle"
              className="h-2"
              aria-labelledby={progressLabelId}
            />
          </div>

          <ol className="flex flex-col gap-3">
            {PROVISIONING_STEPS.map((step, index) => {
              const stepState = getStepState({
                index,
                currentStepIndex,
                isFailed,
              });

              return (
                <li key={step} className="flex items-center gap-2.5">
                  <span
                    aria-hidden
                    className="flex size-5 shrink-0 items-center justify-center"
                  >
                    {stepState === STEP_STATE.DONE && (
                      <span
                        className={cn(
                          STEP_BADGE_CLASS_NAME,
                          DONE_STEP_BADGE_CLASS_NAME,
                        )}
                      >
                        <Check className="size-4" />
                      </span>
                    )}
                    {stepState === STEP_STATE.RUNNING && (
                      <Spinner className="stext-primary-default" />
                    )}
                    {stepState === STEP_STATE.FAILED && (
                      <span
                        className={cn(
                          STEP_BADGE_CLASS_NAME,
                          FAILED_STEP_BADGE_CLASS_NAME,
                        )}
                      >
                        <Xmark className="size-4" />
                      </span>
                    )}
                    {stepState === STEP_STATE.PENDING && (
                      <span
                        className={cn(
                          STEP_BADGE_CLASS_NAME,
                          PENDING_STEP_BADGE_CLASS_NAME,
                        )}
                      >
                        {index + 1}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'text-sm',
                      stepState === STEP_STATE.DONE && 'stext-base-default',
                      stepState === STEP_STATE.RUNNING &&
                        'font-semibold stext-primary-default',
                      stepState === STEP_STATE.FAILED &&
                        'font-semibold stext-danger-default',
                      stepState === STEP_STATE.PENDING && 'stext-muted-default',
                    )}
                  >
                    <SafeFormattedMessageWithoutSpread
                      message={getStepMessage(step)}
                    />
                  </span>
                  <span className="sr-only">
                    <SafeFormattedMessageWithoutSpread
                      message={getStepStateMessage(stepState)}
                    />
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </main>
    </div>
  );
};

OnboardingSetUp.displayName = 'OnboardingSetUp';

export default OnboardingSetUp;
